import { useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Copy,
  Send,
  Check,
  X,
  FileText,
} from 'lucide-react'
import {
  DataTable,
  ColumnVisibilityMenu,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@/components/ui/data-table'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import { EstadoPresupuestoBadge } from '@/components/presupuestos/EstadoPresupuestoBadge'
import {
  formatNumeroPresupuesto,
  isPresupuestoVencido,
  isPresupuestoEditable,
  formatCuit,
} from '@/components/presupuestos/presupuesto-utils'
import { CopyableCell } from '@/components/ui/copyable-cell'
import type { Presupuesto, EstadoPresupuesto } from '@/types/database'
import { formatCurrency, formatDate } from '@/lib/utils'

interface PresupuestosTableProps {
  presupuestos: Presupuesto[]
  onVerDetalle: (presupuesto: Presupuesto) => void
  onEditar: (presupuesto: Presupuesto) => void
  onEliminar: (presupuesto: Presupuesto) => void
  onDuplicar: (presupuesto: Presupuesto) => void
  onCambiarEstado: (presupuesto: Presupuesto, estado: EstadoPresupuesto) => void
  onRowClick?: (presupuesto: Presupuesto) => void
  isLoading?: boolean
  canEdit?: boolean
  canDelete?: boolean
  canCreate?: boolean
  canEnviar?: boolean
  canAceptar?: boolean
  canRechazar?: boolean
}

export function PresupuestosTable({
  presupuestos,
  onVerDetalle,
  onEditar,
  onEliminar,
  onDuplicar,
  onCambiarEstado,
  onRowClick,
  isLoading,
  canEdit = true,
  canDelete = true,
  canCreate = true,
  canEnviar = true,
  canAceptar = true,
  canRechazar = true,
}: PresupuestosTableProps) {
  const {
    visibility,
    saveVisibility,
    columnSizing,
    saveColumnSizing,
    resetVisibility,
    resetColumnSizing,
    resetAll,
  } = useTablePreferences('presupuestos', {
    defaultVisibility: {
      numero: true,
      fecha: true,
      cliente: true,
      cuit: true,
      total: true,
      estado: true,
      validez: true,
    },
  })

  const columns = useMemo<ColumnDef<Presupuesto>[]>(
    () => [
      {
        id: 'numero',
        accessorKey: 'numero_presupuesto',
        header: 'N°',
        meta: { label: 'Número' },
        size: 100,
        minSize: 80,
        maxSize: 140,
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {formatNumeroPresupuesto(row.original.numero_presupuesto)}
          </span>
        ),
      },
      {
        id: 'fecha',
        accessorKey: 'fecha',
        header: 'Fecha',
        meta: { label: 'Fecha' },
        size: 130,
        minSize: 100,
        maxSize: 180,
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.fecha || new Date().toISOString())}
          </span>
        ),
      },
      {
        id: 'cliente',
        accessorKey: 'cliente_nombre',
        header: 'Cliente',
        meta: { label: 'Cliente' },
        size: 200,
        minSize: 120,
        maxSize: 350,
        cell: ({ row }) => (
          <span className="font-medium truncate">{row.original.cliente_nombre}</span>
        ),
      },
      {
        id: 'cuit',
        accessorKey: 'cliente_cuit',
        header: 'CUIT',
        meta: { label: 'CUIT' },
        size: 150,
        minSize: 120,
        maxSize: 180,
        cell: ({ row }) => {
          const cuit = row.original.cliente_cuit
          if (!cuit) return <span className="text-muted-foreground">-</span>
          const clean = cuit.replace(/\D/g, '')
          return (
            <CopyableCell
              value={clean}
              displayValue={formatCuit(cuit)}
            />
          )
        },
      },
      {
        id: 'total',
        accessorKey: 'total',
        header: 'Total',
        meta: { label: 'Total' },
        size: 140,
        minSize: 100,
        maxSize: 200,
        cell: ({ row }) => (
          <span className="block text-right font-medium tabular-nums">
            {formatCurrency(row.original.total || 0)}
          </span>
        ),
      },
      {
        id: 'estado',
        accessorKey: 'estado',
        header: 'Estado',
        meta: { label: 'Estado' },
        size: 200,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => {
          const p = row.original
          const vencido = isPresupuestoVencido(
            p.fecha,
            p.validez_dias,
            p.estado
          )

          return (
            <div className="flex items-center gap-2 flex-wrap">
              <EstadoPresupuestoBadge estado={(p.estado as EstadoPresupuesto) || 'borrador'} />
              {vencido && (
                <Badge variant="destructive" className="text-xs">
                  Vencido
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'validez',
        accessorKey: 'validez_dias',
        header: 'Validez',
        meta: { label: 'Validez' },
        size: 100,
        minSize: 80,
        maxSize: 150,
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.validez_dias} días
          </span>
        ),
      },
      {
        id: 'acciones',
        header: '',
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          const p = row.original
          const esEditable = isPresupuestoEditable(p.estado)
          const estaVencido = isPresupuestoVencido(p.fecha, p.validez_dias, p.estado)

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onVerDetalle(p)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver Detalle
                  </DropdownMenuItem>

                  {/* Editar: solo visible si es borrador y tiene permiso */}
                  {canEdit && esEditable && (
                    <DropdownMenuItem onClick={() => onEditar(p)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                  )}

                  {canCreate && (
                    <DropdownMenuItem onClick={() => onDuplicar(p)}>
                      <Copy className="mr-2 h-4 w-4" />
                      Duplicar
                    </DropdownMenuItem>
                  )}

                  {/* Transiciones de estado (ORR-200 B8: flag fino por acción) */}
                  {((p.estado === 'borrador' && canEnviar) ||
                    (p.estado === 'enviado' && (canAceptar || canRechazar))) && (
                    <>
                      <DropdownMenuSeparator />
                      {p.estado === 'borrador' && canEnviar && (
                        <DropdownMenuItem onClick={() => onCambiarEstado(p, 'enviado')}>
                          <Send className="mr-2 h-4 w-4" />
                          Marcar como Enviado
                        </DropdownMenuItem>
                      )}
                      {p.estado === 'enviado' && canAceptar && (
                        <DropdownMenuItem
                          onClick={() => onCambiarEstado(p, 'aceptado')}
                          disabled={estaVencido}
                          className="text-green-600"
                        >
                          <Check className="mr-2 h-4 w-4" />
                          Marcar como Aceptado
                        </DropdownMenuItem>
                      )}
                      {p.estado === 'enviado' && canRechazar && (
                        <DropdownMenuItem
                          onClick={() => onCambiarEstado(p, 'rechazado')}
                          className="text-red-600"
                        >
                          <X className="mr-2 h-4 w-4" />
                          Marcar como Rechazado
                        </DropdownMenuItem>
                      )}
                    </>
                  )}

                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onEliminar(p)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [onVerDetalle, onEditar, onEliminar, onDuplicar, onCambiarEstado, canEdit, canDelete, canCreate, canEnviar, canAceptar, canRechazar]
  )

  const table = useReactTable({
    data: presupuestos,
    columns,
    state: {
      columnVisibility: visibility,
      columnSizing,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'numero', desc: true }],
      pagination: { pageSize: 50 },
    },
  })

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex justify-end shrink-0">
        <ColumnVisibilityMenu
          table={table}
          onResetVisibility={resetVisibility}
          onResetColumnSizing={resetColumnSizing}
          onResetAll={resetAll}
        />
      </div>
      <div className="flex-1 min-h-0">
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyMessage="No hay presupuestos para mostrar"
          emptyIcon={<FileText className="h-6 w-6 text-muted-foreground" />}
          withVerticalDividers
          stickyRightColumns={['acciones']}
          enableColumnResizing
          enableVerticalScroll
          showPagination
          onRowClick={onRowClick}
        />
      </div>
    </div>
  )
}
